import React, { useState, useEffect } from 'react'
import { useEventsStore } from '../../stores/eventsStore'
import { useAuthStore } from '../../stores/authStore'
import { useFamilyStore } from '../../stores/familyStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { getTypeColor } from '../../utils/eventTypeColors'
import { useEntitlements } from '../../hooks/useEntitlements'

interface EventModalProps {
  isOpen: boolean
  onClose: () => void
  defaultDate: Date
  childId: string
  eventId?: string
}

export const EventModal: React.FC<EventModalProps> = ({ isOpen, onClose, defaultDate, childId, eventId }) => {
  const { addEvent, updateEvent, events } = useEventsStore()
  const { user } = useAuthStore()
  const { family } = useFamilyStore()
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const preferredCurrency = useUserPreferencesStore(state => state.preferences.preferred_currency)
  const entitlements = useEntitlements(family?.id)

  const existing = events.find(e => e.id === eventId)

  const normalizeCurrency = (value?: string | null, fallback: 'USD' | 'EUR' = 'USD'): 'USD' | 'EUR' => {
    if (value === 'USD' || value === 'EUR') return value
    return fallback
  }

  const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0,16)
  const fromLocalInput = (s: string) => new Date(s)

  const roundTo30 = (date: Date) => {
    const d = new Date(date)
    const mins = d.getMinutes()
    d.setMinutes(mins < 30 ? 30 : 60, 0, 0)
    return d
  }

  const [title, setTitle] = useState(existing?.title || '')
  // New semantics: 'note' = legacy 'event'; 'expense' unchanged.
  const initialType = existing?.type === 'event' ? 'note' : (existing?.type || 'note')
  const [type, setType] = useState<'note' | 'expense'>(initialType as 'note' | 'expense')
  const [allDay, setAllDay] = useState(existing?.all_day || false)
  const [startAt, setStartAt] = useState(() => toLocalInput(existing ? new Date(existing.start_at) : roundTo30(defaultDate)))
  const [endAt, setEndAt] = useState(() => toLocalInput(existing && existing.end_at ? new Date(existing.end_at) : new Date(roundTo30(defaultDate).getTime() + 60*60*1000)))
  // Note duration (in minutes). UI uses selector instead of manual end.
  const deriveInitialDuration = () => {
    if (existing && existing.end_at) {
      const startMs = new Date(existing.start_at).getTime()
      const endMs = new Date(existing.end_at).getTime()
      const diffMin = Math.max(0, Math.round((endMs - startMs) / 60000))
      // Allowed values: 30 min, 60, 120, 180, ..., 720
      const allowed = [30, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, 660, 720]
      const chosen = allowed.find(v => v >= diffMin) || 720
      return chosen
    }
    return 60 // default 1 hour
  }
  const [durationMinutes, setDurationMinutes] = useState<number>(deriveInitialDuration())
  const [location, setLocation] = useState(existing?.location || '')
  const [notes, setNotes] = useState(existing?.notes || '')
  const [amount, setAmount] = useState<number | ''>(existing?.amount ?? '')
  const [currency, setCurrency] = useState<'USD' | 'EUR'>(normalizeCurrency(existing?.currency as any, preferredCurrency || 'USD'))
  // category unused – simplified per requirements
  const [isSaving, setIsSaving] = useState(false)

  // Update default dates when opening modal for new event
  useEffect(() => {
    if (isOpen && !existing) {
      const start = roundTo30(defaultDate)
      const end = new Date(start.getTime() + 60*60*1000)
      setStartAt(toLocalInput(start))
      setEndAt(toLocalInput(end))
      setDurationMinutes(60)
      // Reset fields for new entry
      setTitle('')
      setLocation('')
      setNotes('')
      setAmount('')
      setCurrency(preferredCurrency || 'USD')
      setAllDay(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultDate, eventId])

  useEffect(() => {
    if (existing) {
      setTitle(existing.title)
      setType((existing.type === 'event' ? 'note' : existing.type) as any)
      setAllDay(existing.all_day)
      setStartAt(toLocalInput(new Date(existing.start_at)))
      setEndAt(existing.end_at ? toLocalInput(new Date(existing.end_at)) : toLocalInput(new Date(roundTo30(defaultDate).getTime() + 60*60*1000)))
      setDurationMinutes(deriveInitialDuration())
      setLocation(existing.location || '')
      setNotes(existing.notes || '')
      setAmount(existing.amount ?? '')
      setCurrency(normalizeCurrency(existing.currency as any, preferredCurrency || 'USD'))
      
    }
  }, [eventId])

  // When start or duration changes, recalculate end (for notes and not all day)
  useEffect(() => {
    if (type === 'note' && !allDay && startAt && durationMinutes) {
      const startDate = fromLocalInput(startAt)
      const newEnd = new Date(startDate.getTime() + durationMinutes * 60000)
      setEndAt(toLocalInput(newEnd))
    }
  }, [type, allDay, startAt, durationMinutes])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !family || !childId) return
    // Block adding in Basic plan (editing existing remains allowed)
    if (!existing && !entitlements.canAddEvents) {
      return
    }
    setIsSaving(true)
    try {
      // Validation per type
      if (type === 'expense') {
        if (amount === '' || Number(amount) <= 0) {
          setIsSaving(false)
          return
        }
      }
      if (type !== 'note') {
        // expenses: single date, no end_at, all_day=false
        const base = {
          family_id: family.id,
          child_id: childId,
          title: title.trim() || 'Expense',
          notes: notes?.trim() || null,
          start_at: fromLocalInput(startAt).toISOString() as any,
          end_at: null as any,
          all_day: false,
          location: location?.trim() || null,
          type: 'expense',
          amount: type === 'expense' ? Number(amount) : null,
          currency: type === 'expense' ? currency : 'USD',
          metadata: {} as any,
        }
        if (existing) {
          // Update: do not change created_by
          await updateEvent(existing.id, base as any)
        } else {
          await addEvent({ ...base, created_by: user.id } as any)
        }
        onClose()
        return
      }

      const baseNote = {
        family_id: family.id,
        child_id: childId,
        title: title.trim() || 'Note',
        notes: notes?.trim() || null,
        start_at: fromLocalInput(startAt).toISOString() as any,
        end_at: allDay ? null as any : fromLocalInput(endAt).toISOString() as any,
        all_day: allDay,
        location: location?.trim() || null,
        type: 'note' as const,
        amount: null as any,
        currency: 'USD' as const,
        metadata: {} as any,
      }
      if (existing) {
        // Update: do not overwrite created_by
        await updateEvent(existing.id, baseNote as any)
      } else {
        await addEvent({ ...baseNote, created_by: user.id } as any)
      }
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-2 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-slate-200 mt-[20vh]">
        {/* Top actions */}
        <div className="flex items-center justify-end px-4 pt-3">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-medium rounded-md border-2 border-red-500 text-red-600 bg-white hover:bg-red-50 transition-colors mr-2">Cancel</button>
          <button type="submit" form="event-form" disabled={isSaving || (!existing && !entitlements.canAddEvents)} title={!existing && !entitlements.canAddEvents ? 'Available in Pro plan' : undefined} className="btn btn-sm btn-primary disabled:opacity-60" style={{ width: '90px' }}>{isSaving ? 'Saving...' : (existing ? 'Save' : 'Add')}</button>
        </div>
        {/* Tabs full width */}
        <div className="px-4 pb-3 mt-3 border-b">
          <div className="grid grid-cols-2 gap-2">
            {(['note','expense'] as const).map(t => {
              const active = type === t
              const baseColor = getTypeColor(t, scheme.id)
              const text = active ? 'text-white' : 'text-gray-600'
              const border = active ? 'border-2 border-gray-300' : 'border border-gray-200'
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`relative w-full py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${text} ${border} ${active ? 'shadow-sm' : 'hover:bg-gray-50'}`}
                  style={{ backgroundColor: active ? baseColor : undefined }}
                >
                  {t==='note' ? 'Note' : 'Expense'}
                  {!entitlements.canAddEvents && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white border border-amber-600">Pro</span>
                  )}
                </button>
              )
            })}
          </div>
          {!existing && !entitlements.canAddEvents && (
            <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              Adding events, notes, and expenses is available in Pro plan. <a href="/pricing" className="underline">View Pricing</a>.
            </div>
          )}
        </div>

        <form id="event-form" onSubmit={handleSubmit} className="px-4 py-3">
          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
          </div>

          <div className="min-h-[180px]">
            {type === 'note' ? (
              <>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-700">From</label>
                    <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-700">Duration</label>
                    <select
                      value={durationMinutes}
                      onChange={e => setDurationMinutes(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      disabled={allDay}
                      required={!allDay}
                    >
                      <option value={30}>30 min</option>
                      <option value={60}>1 hr</option>
                      <option value={120}>2 hrs</option>
                      <option value={180}>3 hrs</option>
                      <option value={240}>4 hrs</option>
                      <option value={300}>5 hrs</option>
                      <option value={360}>6 hrs</option>
                      <option value={420}>7 hrs</option>
                      <option value={480}>8 hrs</option>
                      <option value={540}>9 hrs</option>
                      <option value={600}>10 hrs</option>
                      <option value={660}>11 hrs</option>
                      <option value={720}>12 hrs</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <input id="allday" type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
                  <label htmlFor="allday" className="text-sm">All day</label>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium mb-1 text-slate-700">Location</label>
                  <input value={location} onChange={e => setLocation(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-700">Date</label>
                    <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                  </div>
                  {type === 'expense' && (
                    <div>
                      <label className="block text-xs font-medium mb-1 text-slate-700">Amount</label>
                      <div className="flex gap-2">
                        <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                        <select value={currency} onChange={e => setCurrency(e.target.value as 'USD' | 'EUR')} className="px-2 py-2 rounded-md border border-slate-300 focus:outline-none">
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </form>
      </div>
    </div>
  )
}

export default EventModal
